import React, {
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback
} from "react";
import { createPortal } from "react-dom";
import "./CustomTooltip.css";

interface TooltipProps {
    children: React.ReactNode;
    text: string;
    placement?: "top" | "bottom" | "left" | "right";
    offset?: number;
    style?: React.CSSProperties;
    styleContainer?: React.CSSProperties;
    className?: string;
    delay?: number;
}

const Tooltip: React.FC<TooltipProps> = ({
    children,
    text,
    placement = "top",
    offset = 10,
    style,
    styleContainer,
    className,
    delay = 500,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const tooltipRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipContainer = useRef<HTMLDivElement | null>(null);
    const timerId = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        let container = document.getElementById("tooltip-container") as HTMLDivElement;
        if (!container) {
            container = document.createElement("div");
            container.id = "tooltip-container";
            document.body.appendChild(container);
        }
        tooltipContainer.current = container;

        return () => {
            if (timerId.current) {
                clearTimeout(timerId.current);
            }
        };
    }, []);

    const calculatePosition = useCallback(() => {
        if (!triggerRef.current || !tooltipRef.current || !isVisible) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        
        requestAnimationFrame(() => {
            const scrollY = window.scrollY;
            const scrollX = window.scrollX;

            let top = 0;
            let left = 0;

            switch (placement) {
                case "top":
                    top = triggerRect.top + scrollY - tooltipRect.height - offset;
                    left = triggerRect.left + scrollX + (triggerRect.width - tooltipRect.width) / 2;
                    break;
                case "bottom":
                    top = triggerRect.bottom + scrollY + offset;
                    left = triggerRect.left + scrollX + (triggerRect.width - tooltipRect.width) / 2;
                    break;
                case "left":
                    top = triggerRect.top + scrollY + (triggerRect.height - tooltipRect.height) / 2;
                    left = triggerRect.left + scrollX - tooltipRect.width - offset;
                    break;
                case "right":
                    top = triggerRect.top + scrollY + (triggerRect.height - tooltipRect.height) / 2;
                    left = triggerRect.right + scrollX + offset;
                    break;
            }

            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            if (left + tooltipRect.width > viewportWidth + scrollX) {
                left = Math.max(scrollX + 5, viewportWidth + scrollX - tooltipRect.width - 5);
            } else if (left < scrollX) {
                left = scrollX + 5;
            }

            if (top + tooltipRect.height > viewportHeight + scrollY) {
                top = Math.max(scrollY + 5, viewportHeight + scrollY - tooltipRect.height - 5);
            } else if (top < scrollY) {
                top = scrollY + 5;
            }

            setPosition({ top, left });
        });
    }, [placement, offset, isVisible]);

    const handleMouseEnter = useCallback(() => {
        if (timerId.current) {
            clearTimeout(timerId.current);
        }

        timerId.current = setTimeout(() => {
            setIsVisible(true);
        }, delay);
    }, [delay]);

    const handleMouseLeave = useCallback(() => {
        if (timerId.current) {
            clearTimeout(timerId.current);
            timerId.current = null;
        }
        setIsVisible(false);
    }, []);

    useEffect(() => {
        if (!isVisible) return;

        calculatePosition();

        let resizeTimeout: NodeJS.Timeout;
        const handleResize = () => {
            if (resizeTimeout) clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(calculatePosition, 100);
        };

        let scrollTimeout: NodeJS.Timeout;
        const handleScroll = () => {
            if (scrollTimeout) clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(calculatePosition, 16);
        };

        window.addEventListener("resize", handleResize, { passive: true });
        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("scroll", handleScroll);
            if (resizeTimeout) clearTimeout(resizeTimeout);
            if (scrollTimeout) clearTimeout(scrollTimeout);
        };
    }, [isVisible, calculatePosition]);

    const tooltipStyle: React.CSSProperties = useMemo(() => ({
        position: "fixed",
        top: position.top,
        left: position.left,
        backgroundColor: "white",
        color: "black",
        border: "1px solid gray",
        padding: "5px 10px",
        borderRadius: "5px",
        fontSize: "12px",
        zIndex: 10000,
        maxWidth: "600px",
        opacity: isVisible ? 1 : 0,
        transition: "opacity 0.3s ease-in-out",
        pointerEvents: "none" as const,
        visibility: isVisible ? "visible" as const : "hidden" as const,
        ...style,
    }), [position.top, position.left, isVisible, style]);

    const tooltipPortal = useMemo(() => {
        if (!tooltipContainer.current || !text || !isVisible) return null;

        return createPortal(
            <div
                ref={tooltipRef}
                style={tooltipStyle}
                className={`ui__component--tooltip__block ${className || ""}`}
                aria-hidden={!isVisible}
            >
                {text}
            </div>,
            tooltipContainer.current
        );
    }, [tooltipStyle, className, text, isVisible]);

    return (
        <>
            <div
                ref={triggerRef}
                className="ui__component--tooltip__show"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={styleContainer}
                aria-describedby={isVisible ? "tooltip" : undefined}
            >
                {children}
            </div>
            {tooltipPortal}
        </>
    );
};

export default React.memo(Tooltip);